/**
 * 핵심 값(단가·마진·환율·정산·배송비) 변경 확인.
 *
 * 저장 요청이 나가기 직전(axios 요청 인터셉터)에 이 목록과 맞춰 보고, 해당하면
 * CriticalChangeProvider 가 띄우는 확인 창에서 사유와 확인을 받는다. 확인하면
 * X-Critical-Confirm 헤더(base64 JSON)를 붙여 보낸다. 서버(blbent_bubble
 * src/common/critical-change)는 같은 목록으로 검사하고, 확인 없는 요청은 428 로 거부하며,
 * 모든 시도를 critical_change_logs 에 남긴다.
 *
 * 목록을 바꿀 때는 서버 critical-routes.ts 와 함께 고친다. 여기서 빠진 경로가 있어도
 * 서버가 428 을 돌려주면 retryAfterCriticalRefusal 이 확인을 받아 다시 보낸다.
 */

export const CONFIRM_WINDOW_MINUTES = 10;
export const CONFIRM_HEADER = 'X-Critical-Confirm';

/* 끝의 수수료 칸은 이름을 정확히 적는다 — 예전 `fee\w*` 가 feedback·feed·feeId 까지 잡아
   피드백 하나 저장할 때도 «핵심 값» 확인 창이 떴다(2026-10-07 대표님 «번거로울 것 같은데»). 서버 critical-routes.ts 와 같다. */
export const PRICE_KEYS =
  /^(markup|minMargin\w*|maxMargin|arrivalCurrency|roundingUnit|roundingMethod|supplyPriceEnabled|exchangeRate|localVAT|vat|taxType|kolBase\w*|clientBase\w*|kol\w*Price\w*|kolRate\w*|kolBaseRate|kolTaxType|kolTaxIncluded|kolCurrency|client\w*Price\w*|clientSupplyPrice|client\w*AdFee|client\w*Currency|reuse\w*|campaignPrice|contentReusePrice|seedingClientPricePerHead|budget|currency|firstDeposit\w*|secondPayment\w*|contentReuseFirstDeposit\w*|contentReuseSecondPayment\w*|fxRateContract|paidAt|actualRemitKrw|payingEntity|remitChannel|remitBatchId|settlementLocked|deliveryPrice|clientDeliveryPrice|deliveryPriceCurrency|blbExpectedRevenue\w*|blbCurrency|price|amount|\w*Fee|fees?|fee(Rate|ClientPrice|Krw|Total|Min|Amount|Currency))$/i;

const SHIPPING_KEYS = /^(deliveryPrice|clientDeliveryPrice|deliveryPriceCurrency|totalBoxCount|individualDeliveryCount|estimatedShippingCost)$/i;
const ID = '[^/]+';
const W = ['POST', 'PUT', 'PATCH', 'DELETE'];

/** 서버 critical-routes.ts 와 같은 목록 + 화면에 보여 줄 경고 */
export const CRITICAL_ROUTES = [
  { domain: 'client-pricing', label: '환율·마크업·마진 설정', methods: ['POST'], path: /^\/client-pricing\/(exchange-rates|client-settings|seeding-rates|addon-items|rounding-mode)$/, warning: '이 설정은 해당 클라이언트(환율표는 모든 클라이언트)의 이후 공급가 계산에 쓰입니다.' },
  { domain: 'client-pricing', label: '클라이언트 단가 설정', methods: ['PATCH', 'PUT'], path: new RegExp(`^/clients/${ID}$`), keys: PRICE_KEYS },
  { domain: 'rate-plans', label: '캠페인 단가표', methods: W, path: /^\/campaign-rate-plans(\/.*)?$/, warning: '단가표 전체를 한 번에 저장합니다. 이 단가표를 쓰는 모든 견적에 반영됩니다.' },
  { domain: 'kol-base-price', label: 'KOL 기본 단가', methods: ['PATCH'], path: new RegExp(`^/influencers/contents/${ID}/kol-supply-step$`), warning: '같은 콘텐츠가 들어간 다른 캠페인들의 KOL 단가·공급가도 함께 바뀝니다.' },
  { domain: 'kol-base-price', label: 'KOL 콘텐츠 단가', methods: ['PATCH'], path: new RegExp(`^/influencers/contents/${ID}$`), keys: PRICE_KEYS },
  { domain: 'kol-base-price', label: 'KOL 단가 정보', methods: ['PATCH', 'PUT'], path: new RegExp(`^/influencers/${ID}$`), keys: PRICE_KEYS },
  { domain: 'my-kols-price', label: 'My KOLs 단가', methods: ['PATCH'], path: new RegExp(`^/my-kols/${ID}/influencers/${ID}$`), keys: PRICE_KEYS },
  { domain: 'my-kols-price', label: 'My KOLs 일괄 수정', methods: ['PATCH'], path: new RegExp(`^/my-kols/${ID}/bulk-update$`), keys: PRICE_KEYS, warning: '여러 줄을 한꺼번에 바꿉니다.' },
  { domain: 'my-kols-price', label: 'My KOLs 옵션 단가', methods: W, path: new RegExp(`^/my-kols/${ID}/items/${ID}/options(/${ID})?$`) },
  { domain: 'campaign-kol-price', label: '캠페인 KOL 단가', methods: ['PATCH'], path: new RegExp(`^/campaigns/${ID}/influencers/${ID}$`), keys: PRICE_KEYS },
  { domain: 'campaign-kol-price', label: '캠페인 KOL 계약가', methods: ['PATCH'], path: new RegExp(`^/campaigns/${ID}/influencers/${ID}/contract$`) },
  { domain: 'campaign-kol-price', label: '캠페인 KOL 관리 정보', methods: ['PATCH'], path: new RegExp(`^/campaigns/${ID}/management/${ID}(/content-reuse)?$`), keys: PRICE_KEYS },
  { domain: 'campaign-kol-price', label: '캠페인 공급가 재계산', methods: ['POST'], path: new RegExp(`^/campaigns/${ID}/recalculate-client-prices$`), warning: '이 캠페인의 모든 KOL 공급가를 다시 계산해 덮어씁니다.' },
  { domain: 'campaign-kol-price', label: '관리 레코드 단가', methods: ['PATCH'], path: new RegExp(`^/managements/${ID}(/client-contract)?$`), keys: PRICE_KEYS },
  { domain: 'campaign-kol-price', label: '관리 레코드 옵션 단가', methods: ['POST'], path: new RegExp(`^/managements/${ID}/options$`) },
  { domain: 'campaign-price', label: '캠페인 금액', methods: ['PATCH', 'PUT'], path: new RegExp(`^/campaigns/${ID}$`), keys: PRICE_KEYS },
  { domain: 'settlement', label: '캠페인 정산표', methods: ['PATCH'], path: new RegExp(`^/campaigns/${ID}/settlement/(summary-table|detail-table)$`) },
  { domain: 'settlement', label: '정산 문서(단가표·배송 정산·발행·청구)', methods: ['PUT', 'DELETE'], path: /^\/settlement\/docs\/(rate|shipsettle|issued|billing)\/.+$/ },
  /* 초안검수 추가 검수비(3차 수정부터) — 켜고 끄면 청구액이 바뀐다(2026-10-21, 서버 critical-routes.ts 와 같다) */
  { domain: 'settlement', label: '초안검수 추가 검수비', methods: ['PUT'], path: new RegExp(`^/campaigns/${ID}/draft-review/settings$`), keys: /^(extraFeeEnabled|extraFeeRate)$/, warning: '켜면 3차 수정부터 회당 단가가 이 캠페인 청구액에 더해집니다.' },
  { domain: 'shipping-price', label: '배송비·박스 수', methods: ['PATCH', 'PUT'], path: new RegExp(`^/shipping/${ID}$`), keys: SHIPPING_KEYS },
  { domain: 'csv-upload', label: 'CSV 일괄 업로드', methods: ['POST'], path: /^\/csv-upload$/, warning: 'CSV 의 값으로 여러 건의 단가를 한꺼번에 덮어쓸 수 있습니다.' },
];

/** 확인 창에 보여 줄 필드 이름 */
const FIELD_LABELS = {
  markup: '마크업(%)', minMarginOneToOne: '최소 마진(1:1)', minMarginSeeding: '최소 마진(시딩)', minMarginAdditional: '최소 마진(옵션)', maxMargin: '최대 마진',
  arrivalCurrency: '도착 통화', roundingUnit: '반올림 단위', roundingMethod: '반올림 방식', exchangeRate: '환율', rate: '환율',
  kolBaseCurrency: 'KOL 통화', kolBaseCurrentPrice: 'KOL 기본 단가', kolBaseTotalPrice: 'KOL 총액', clientBasePrice: '클라이언트 기본가',
  kolLocalPrice: 'KOL 단가(현지)', kolTotalPrice: 'KOL 총액', kolContractPrice: 'KOL 계약가', clientExpectedPrice: '예상 공급가', clientFinalPrice: '최종 공급가',
  clientSupplyPrice: '공급가', clientTotalAdFee: '광고비 총액', campaignPrice: '캠페인 금액', contentReusePrice: '2차 활용 금액', seedingClientPricePerHead: '시딩 1인 단가',
  budget: '예산', currency: '통화', firstDeposit: '1차 입금', secondPayment: '2차 입금', fxRateContract: '계약 환율', paidAt: '지급일', actualRemitKrw: '실제 송금액(원)',
  settlementLocked: '정산 잠금', deliveryPrice: '배송비', clientDeliveryPrice: '청구 배송비', deliveryPriceCurrency: '배송비 통화', totalBoxCount: '박스 수', individualDeliveryCount: '개별 배송 수',
};

/** 본문(중첩 포함)에서 조건에 맞는 키 이름을 모은다 */
export function collectKeys(body, pattern, depth = 0, out = new Set()) {
  if (!body || typeof body !== 'object' || depth > 4) return out;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return out;
  for (const [k, v] of Object.entries(body)) {
    if (!/^\d+$/.test(k) && pattern.test(k)) out.add(k);
    if (v && typeof v === 'object') collectKeys(v, pattern, depth + 1, out);
  }
  return out;
}

export function normalizePath(url) {
  let p = String(url || '');
  try { p = new URL(p, 'http://x').pathname; } catch { p = p.split('?')[0]; }
  p = p.replace(/\/+$/, '');
  return p.replace(/^\/api(?=\/|$)/, '') || '/';
}

function parseBody(data) {
  if (typeof data === 'string') { try { return JSON.parse(data); } catch { return null; } }
  return data;
}

export function matchCritical(method, url, data) {
  const m = String(method || '').toUpperCase();
  if (!W.includes(m)) return null;
  const p = normalizePath(url);
  const body = parseBody(data);
  for (const route of CRITICAL_ROUTES) {
    if (!route.methods.includes(m) || !route.path.test(p)) continue;
    if (!route.keys) return { route, fields: [...collectKeys(body, PRICE_KEYS)] };
    const fields = [...collectKeys(body, route.keys)];
    if (fields.length) return { route, fields };
  }
  return null;
}

/** 확인 창에 보여 줄 "저장될 값" — 금액 관련 키만, 최대 12줄 */
export function describePayload(data, fields = []) {
  const body = parseBody(data);
  const want = new Set(fields);
  const rows = [];
  const walk = (v, prefix, depth) => {
    if (!v || typeof v !== 'object' || depth > 3 || rows.length >= 12) return;
    for (const [k, x] of Object.entries(v)) {
      if (rows.length >= 12) return;
      if (x && typeof x === 'object') walk(x, /^\d+$/.test(k) ? `${prefix}#${Number(k) + 1} ` : prefix, depth + 1);
      else if (want.has(k)) rows.push({ key: `${prefix}${k}${rows.length}`, label: `${prefix}${FIELD_LABELS[k] || k}`, value: x === null || x === '' ? '(비움)' : typeof x === 'number' ? x.toLocaleString('ko-KR') : String(x) });
    }
  };
  walk(body, '', 0);
  return rows;
}

// ── 확인 상태 ─────────────────────────────────────────────
// 확인은 도메인(종류)별로 CONFIRM_WINDOW_MINUTES 동안 유효하다. 새로고침하면 사라진다(메모리에만 둔다).

let confirmHandler = null;
const sessions = new Map(); // domain → { id, label, reason, confirmedAt, expiresAt }
const inflight = new Map(); // domain → Promise(session|null) — 같은 종류 요청이 동시에 여러 개 나가도 창은 하나만
const listeners = new Set();

function emit() {
  const list = [...sessions.values()];
  listeners.forEach((fn) => fn(list));
}

export function setConfirmHandler(fn) { confirmHandler = fn; }

export function subscribeSessions(fn) {
  listeners.add(fn);
  fn([...sessions.values()]);
  return () => listeners.delete(fn);
}

export function endAllSessions() {
  sessions.clear();
  emit();
}

function liveSession(domain) {
  const s = sessions.get(domain);
  if (s && s.expiresAt > Date.now()) return s;
  if (s) { sessions.delete(domain); emit(); }
  return null;
}

function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function encodeConfirm(session) {
  const json = JSON.stringify({ id: session.id, reason: session.reason, confirmedAt: session.confirmedAt });
  // UTF-8 바이트를 base64 로 — 헤더에는 ASCII 만 실을 수 있다
  return btoa(unescape(encodeURIComponent(json)));
}

async function obtainSession(match, config) {
  const { route } = match;
  const live = liveSession(route.domain);
  if (live) return live;
  if (inflight.has(route.domain)) return inflight.get(route.domain);
  const p = (async () => {
    if (!confirmHandler) return null; // 확인 창이 없는 곳(테스트 등)에서는 확인을 붙이지 않는다 — 서버가 판단한다
    const answer = await confirmHandler({
      domain: route.domain,
      label: route.label,
      warning: route.warning,
      fields: match.fields,
      method: String(config.method || '').toUpperCase(),
      url: config.url,
      data: config.data,
    });
    if (!answer) return null;
    const now = Date.now();
    const session = { id: newId(), domain: route.domain, label: route.label, reason: answer.reason, confirmedAt: new Date(now).toISOString(), expiresAt: now + CONFIRM_WINDOW_MINUTES * 60000 };
    sessions.set(route.domain, session);
    emit();
    return session;
  })();
  inflight.set(route.domain, p);
  try { return await p; } finally { inflight.delete(route.domain); }
}

export class CriticalChangeCancelled extends Error {
  constructor(label) {
    super(`핵심 값 변경을 취소했습니다: ${label}`);
    this.name = 'CriticalChangeCancelled';
    this.isCriticalCancel = true;
  }
}

/**
 * 저장하는 쪽이 «먼저» 확인을 받는다 — 수정을 다 마친 뒤 저장 단추를 눌렀을 때 바뀐 값을 요약해 보여 주고
 * 한 번만 묻는다(2026-10-23 사용자: «수정 → 모든 수정이 마무리 → 팝업(수정된 내용 간단하게 표시) → 수락하면 반영»).
 * 예전에는 첫 수정 직후 저장 요청이 나가는 순간 창이 떴고, 확인하면 10분간 «편집 중» 띠가 남았다.
 *
 * 돌려주는 값을 요청 설정의 `criticalSession` 에 실으면 인터셉터가 같은 확인을 헤더로 붙인다(요청이 여러 개여도 확인은 한 번).
 * 서버가 받는 헤더·기록(사유 포함)은 그대로다. 10분 유효 시간·편집 중 띠는 만들지 않는다 — 저장 한 번이 확인 한 번이다.
 *
 * @param {{label: string, warning?: string, summary: Array<{scope?: string, label: string, before?: string, after?: string}>}} info
 * @returns {Promise<object|null>} 확인 세션 · 취소하면 null · 확인 창이 없는 곳(시험 등)이면 { none: true }
 */
export async function askCriticalConfirm({ label, warning, summary = [] }) {
  if (!confirmHandler) return { none: true };
  const answer = await confirmHandler({ domain: 'staged', label, warning, summary, once: true, fields: [], method: 'PUT', data: null });
  if (!answer) return null;
  const now = Date.now();
  return { id: newId(), domain: 'staged', label, reason: answer.reason, confirmedAt: new Date(now).toISOString(), expiresAt: now };
}

/** axios 요청 인터셉터에서 부른다. 해당 없으면 config 그대로. */
export async function attachCriticalConfirm(config) {
  /* 저장하는 쪽이 이미 확인을 받았으면(askCriticalConfirm) 그 확인을 붙이고 끝낸다 — 창을 또 띄우지 않는다 */
  if (config.criticalSession) {
    const s = config.criticalSession;
    if (!s.none) {
      config.headers = config.headers || {};
      if (typeof config.headers.set === 'function') config.headers.set(CONFIRM_HEADER, encodeConfirm(s));
      else config.headers[CONFIRM_HEADER] = encodeConfirm(s);
    }
    return config;
  }
  const match = matchCritical(config.method, config.url, config.data);
  if (!match) return config;
  const session = await obtainSession(match, config);
  if (!session) {
    if (!confirmHandler) return config;
    throw new CriticalChangeCancelled(match.route.label);
  }
  config.headers = config.headers || {};
  if (typeof config.headers.set === 'function') config.headers.set(CONFIRM_HEADER, encodeConfirm(session));
  else config.headers[CONFIRM_HEADER] = encodeConfirm(session);
  return config;
}

/** axios 응답 오류에서 부른다. 서버가 확인 없는 변경이라며 거부(428)하면 확인을 받아 한 번 다시 보낸다. */
export async function retryAfterCriticalRefusal(error, client) {
  const res = error?.response;
  const cfg = error?.config;
  if (!res || res.status !== 428 || !cfg || cfg.__criticalRetried) return null;
  const code = res.data?.error?.code;
  if (code && code !== 'CRITICAL_CONFIRM_REQUIRED') return null;
  const d = res.data?.error?.details || {};
  const domain = d.domain || 'unknown';
  sessions.delete(domain);
  emit();
  const route = CRITICAL_ROUTES.find((r) => r.domain === domain) || { domain, label: d.label || '핵심 값 변경' };
  const session = await obtainSession({ route: { ...route, label: d.label || route.label }, fields: d.fields || [] }, cfg);
  if (!session) return null;
  cfg.__criticalRetried = true;
  cfg.headers = cfg.headers || {};
  if (typeof cfg.headers.set === 'function') cfg.headers.set(CONFIRM_HEADER, encodeConfirm(session));
  else cfg.headers[CONFIRM_HEADER] = encodeConfirm(session);
  return client.request(cfg);
}

/** 테스트용 — 상태를 비운다 */
export function __resetCriticalChange() {
  sessions.clear();
  inflight.clear();
  listeners.clear();
  confirmHandler = null;
}
